/**
 * Company CNAEs Tab Component
 * Displays and manages company CNAEs as a compact list
 */

import React, { useCallback, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Chip,
  Typography,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Factory as FactoryIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import {
  CompanyCnae,
  CompanyCnaeFormData,
  formatCnaeCode,
} from '../../../../types/company';
import { useListaPermitida } from '../../../../authorization/useListaPermitida';
import { companyService } from '../../../../services/companyService';
import { CnaeFormModal } from '../modals';
import { ActionButton, ProfileSection } from '../../../../components/common';

interface CompanyCnaesTabProps {
  companyId: string;
}

const CompanyCnaesTab: React.FC<CompanyCnaesTabProps> = ({
  companyId,
}) => {
  const { enqueueSnackbar } = useSnackbar();


  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCnae, setSelectedCnae] = useState<CompanyCnae | undefined>();
  const [modalError, setModalError] = useState<string | null>(null);

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [cnaeToDelete, setCnaeToDelete] = useState<CompanyCnae | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Gerir o CNAE da empresa é permissão própria, e quem só lê a empresa não a tem: a aba
  // vinha com alerta vermelho de erro para uma recusa que não é falha nenhuma.
  const carregarCnaes = useCallback(() => companyService.getCnaes(companyId), [companyId]);
  const lista = useListaPermitida<CompanyCnae>('company.manage-cnaes', 'CNAEs', carregarCnaes);
  const cnaes = lista.itens;
  const loading = lista.carregando;
  const loadCnaes = lista.recarregar;

  // Handle add CNAE
  const handleAdd = () => {
    setSelectedCnae(undefined);
    setModalError(null);
    setModalOpen(true);
  };

  // Handle edit CNAE
  const handleEdit = (cnae: CompanyCnae) => {
    setSelectedCnae(cnae);
    setModalError(null);
    setModalOpen(true);
  };

  // Handle delete CNAE
  const handleDeleteClick = (cnae: CompanyCnae) => {
    setCnaeToDelete(cnae);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!cnaeToDelete) return;

    setDeleteLoading(true);
    try {
      await companyService.deleteCnae(companyId, cnaeToDelete.id.toString());
      enqueueSnackbar('CNAE excluido com sucesso', { variant: 'success' });
      loadCnaes();
    } catch (err: any) {
      enqueueSnackbar(err.message ?? 'Erro ao excluir CNAE', { variant: 'error' });
    } finally {
      setDeleteLoading(false);
      setDeleteDialogOpen(false);
      setCnaeToDelete(null);
    }
  };

  // Handle set as primary
  const handleSetPrimary = async (cnae: CompanyCnae) => {
    if (cnae.isPrimary) return;

    try {
      await companyService.setCnaePrimary(companyId, cnae.id.toString());
      enqueueSnackbar('CNAE definido como principal', { variant: 'success' });
      loadCnaes();
    } catch (err: any) {
      enqueueSnackbar(err.message ?? 'Erro ao definir CNAE como principal', { variant: 'error' });
    }
  };

  // Handle form submit
  const handleFormSubmit = async (data: CompanyCnaeFormData) => {
    setModalError(null);
    try {
      const payload = {
        cnaeCode: data.cnaeCode,
        description: data.description,
        isPrimary: data.isPrimary,
      };

      if (selectedCnae) {
        await companyService.updateCnae(
          companyId,
          selectedCnae.id.toString(),
          payload
        );
        enqueueSnackbar('CNAE atualizado com sucesso', { variant: 'success' });
      } else {
        await companyService.createCnae(companyId, payload);
        enqueueSnackbar('CNAE adicionado com sucesso', { variant: 'success' });
      }
      loadCnaes();
    } catch (err: any) {
      setModalError(err.message ?? 'Erro ao salvar CNAE');
      throw err;
    }
  };

  return (
    <ProfileSection
      title="CNAEs"
      icon={<FactoryIcon />}
      action={
        <ActionButton
          acao="company.manage-cnaes"
          rotulo="Adicionar CNAE"
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAdd}
        >
          Adicionar
        </ActionButton>
      }
    >
      {lista.erro && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {lista.erro}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : cnaes.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary" gutterBottom>
            {lista.bloqueio ?? 'Nenhum CNAE cadastrado'}
          </Typography>
          {lista.permitido && (
            <Button size="small" startIcon={<AddIcon />} onClick={handleAdd}>
              Adicionar CNAE
            </Button>
          )}
        </Box>
      ) : (
        <Box>
          {cnaes.map((cnae, index) => (
            <Box
              key={cnae.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                py: 1.5,
                borderBottom: index < cnaes.length - 1 ? '1px solid' : 'none',
                borderColor: 'divider',
              }}
            >
              <Tooltip title={cnae.isPrimary ? 'CNAE Principal' : 'Definir como Principal'}>
                <IconButton
                  size="small"
                  onClick={() => handleSetPrimary(cnae)}
                  color={cnae.isPrimary ? 'warning' : 'default'}
                  sx={{ mr: 1 }}
                >
                  {cnae.isPrimary ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Typography variant="body2" fontFamily="monospace" sx={{ minWidth: 110 }}>
                {formatCnaeCode(cnae.cnaeCode)}
              </Typography>
              {cnae.isPrimary && (
                <Chip label="Principal" size="small" color="warning" sx={{ mr: 1 }} />
              )}
              <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                {cnae.description}
              </Typography>
              <Tooltip title="Editar">
                <IconButton size="small" onClick={() => handleEdit(cnae)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Excluir">
                <IconButton size="small" color="error" onClick={() => handleDeleteClick(cnae)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
        </Box>
      )}

      {/* Form Modal */}
      <CnaeFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleFormSubmit}
        cnae={selectedCnae}
        error={modalError}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmar Exclusao</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja excluir o CNAE &quot;{cnaeToDelete?.cnaeCode}&quot;?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteLoading}
          >
            {deleteLoading ? 'Excluindo...' : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>
    </ProfileSection>
  );
};

export default CompanyCnaesTab;
